import React from "react";
import * as MUI from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import MedicationIcon from "@mui/icons-material/Medication";
import { useTranslation } from "react-i18next";
import DetailRow from "../../../../components/display/DetailRow";

function DialogHeader({ template, canEdit, canDelete, onClose, onEdit, onDelete }) {
  const { t } = useTranslation();

  return (
    <MUI.DialogTitle sx={{ p: { xs: 2.5, sm: 3.5 }, pt: { xs: 1, sm: 1.25 } }}>
      <MUI.Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MUI.Stack direction="row" spacing={0.5}>
          {canDelete && (
            <MUI.Tooltip title={t("Delete")}>
              <MUI.IconButton
                onClick={() => onDelete(template)}
                size="small"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </MUI.IconButton>
            </MUI.Tooltip>
          )}
          {canEdit && (
            <MUI.Tooltip title={t("Edit")}>
              <MUI.IconButton onClick={() => onEdit(template)} size="small">
                <EditIcon fontSize="small" />
              </MUI.IconButton>
            </MUI.Tooltip>
          )}
        </MUI.Stack>

        <MUI.IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.Stack>

      <MUI.Stack direction="row" spacing={2} alignItems="center">
        <MUI.Avatar
          variant="rounded"
          sx={{
            bgcolor: "primary.main",
            width: 52,
            height: 52,
            borderRadius: 2.5,
          }}
        >
          <MedicationIcon />
        </MUI.Avatar>
        <MUI.Box sx={{ minWidth: 0 }}>
          <MUI.Typography
            variant="h6"
            sx={{ fontWeight: 700, lineHeight: 1.25 }}
            noWrap
          >
            {template?.title}
          </MUI.Typography>
          <MUI.Chip
            label={`${t("ID")}: ${template?.id}`}
            size="small"
            color="primary"
            sx={{ mt: 0.5 }}
          />
        </MUI.Box>
      </MUI.Stack>
    </MUI.DialogTitle>
  );
}

function DialogBody({ template }) {
  const { t } = useTranslation();

  return (
    <MUI.DialogContent
      dividers
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        bgcolor: (theme) =>
          theme.palette.mode === "light"
            ? "grey.50"
            : "rgba(255,255,255,0.02)",
      }}
    >
      <MUI.Stack spacing={2.5}>
        <DetailRow label={t("ManufacturerName")} value={template?.manufacturerName} />
        <DetailRow label={t("GenericName")} value={template?.genericName} />
        <DetailRow label={t("DescriptionWhy")} value={template?.descriptionWhy} />
        <DetailRow label={t("DescriptionHowToApply")} value={template?.descriptionHowToApply} />
        <DetailRow label={t("Karenca")} value={template?.karenca} />
        <DetailRow label={t("DispensingCode")} value={template?.dispensingCode} />
      </MUI.Stack>
    </MUI.DialogContent>
  );
}

export default function PrescriptionTemplateDetailsDialog({
  template,
  canEdit,
  canDelete,
  onClose,
  onEdit,
  onDelete,
}) {
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));

  return (
    <MUI.Dialog
      open={Boolean(template)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <DialogHeader
        template={template}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={onClose}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      <DialogBody template={template} />
    </MUI.Dialog>
  );
}
